import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import AlertComponent from "../../components/AlertComponent";
import TargetSettingsModal from "./TargetSettingsModal";

const FILTROS_TIPO = [
  { key: "todos", label: "Todos" },
  { key: "producao", label: "Produção" },
  { key: "homologacao", label: "Homologação" },
];

const FILTROS_STATUS = [
  { key: "ativos", label: "Ativos" },
  { key: "inativos", label: "Inativos" },
  { key: "todos", label: "Todos" },
];

function matchesFiltros(target, tipo, status, machineId) {
  if (tipo === "producao" && !target.isProduction) return false;
  if (tipo === "homologacao" && target.isProduction) return false;
  if (status === "ativos" && target.isActive === false) return false;
  if (status === "inativos" && target.isActive !== false) return false;
  if (!machineId || String(target.deploymentMachineId) !== String(machineId)) return false;
  return true;
}

function VioletSwitch({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 ${
        checked ? "bg-violet-600" : "bg-slate-300"
      } ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform translate-y-1 ${
          checked ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );
}

function parseBinding(raw) {
  if (!raw || typeof raw !== "string") return null;
  const match = raw.match(/^([^:]*):([^:]*):(.*?)\s*\[(\w+)\]\s*$/);
  if (!match) return { raw, ip: null, port: null, host: null, protocol: null };
  const [, ip, port, host, protocol] = match;
  return { raw, ip: ip || null, port: port || null, host: host || null, protocol: (protocol || "").toLowerCase() };
}

function bindingUrl(b) {
  if (!b || !b.protocol) return null;
  const host = (b.host && b.host.trim()) || (b.ip && b.ip !== "*" ? b.ip : null);
  if (!host) return null;
  const isDefaultPort =
    (b.protocol === "https" && b.port === "443") ||
    (b.protocol === "http" && b.port === "80");
  return isDefaultPort ? `${b.protocol}://${host}` : `${b.protocol}://${host}:${b.port}`;
}

function bindingLabel(b) {
  if (!b) return null;
  const host = (b.host && b.host.trim()) || (b.ip && b.ip !== "*" ? b.ip : null);
  if (!host) return b.port ? `*:${b.port}` : null;
  const isDefaultPort =
    (b.protocol === "https" && b.port === "443") ||
    (b.protocol === "http" && b.port === "80");
  return isDefaultPort ? host : `${host}:${b.port}`;
}

const STATE_STYLES = {
  started: { label: "Ativo", pill: "bg-emerald-100 text-emerald-700 ring-emerald-200", dot: "bg-emerald-500" },
  stopped: { label: "Parado", pill: "bg-red-100 text-red-700 ring-red-200", dot: "bg-red-500" },
  starting: { label: "Iniciando", pill: "bg-amber-100 text-amber-700 ring-amber-200", dot: "bg-amber-500 animate-pulse" },
  stopping: { label: "Parando", pill: "bg-amber-100 text-amber-700 ring-amber-200", dot: "bg-amber-500 animate-pulse" },
};

function stateStyle(state) {
  const key = (state || "").toLowerCase();
  return STATE_STYLES[key] || {
    label: state || "Desconhecido",
    pill: "bg-slate-100 text-slate-600 ring-slate-200",
    dot: "bg-slate-400",
  };
}

function formatarDataHora(data) {
  if (!data) return "—";
  return new Date(data).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "America/Sao_Paulo",
  });
}

export default function AmbientesList() {
  const [ambientes, setAmbientes] = useState([]);
  const [sites, setSites] = useState([]);
  const [machines, setMachines] = useState([]);
  const [gitConfigs, setGitConfigs] = useState([]);
  const [buildConfigs, setBuildConfigs] = useState([]);
  const [restoreConfigs, setRestoreConfigs] = useState([]);
  const [s3Configs, setS3Configs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [iisBusyId, setIisBusyId] = useState(null);
  const [filtroTipo, setFiltroTipo] = useState("todos");
  const [filtroStatus, setFiltroStatus] = useState("ativos");
  const [filtroMachine, setFiltroMachine] = useState("");

  const machinesOrdenadas = useMemo(
    () => [...machines].sort((a, b) => a.id - b.id),
    [machines]
  );

  useEffect(() => {
    if (!filtroMachine && machinesOrdenadas.length > 0) {
      setFiltroMachine(String(machinesOrdenadas[0].id));
    }
  }, [machinesOrdenadas, filtroMachine]);
  const [editingTargetId, setEditingTargetId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [targetsRes, machinesRes, gitRes, buildRes, restoreRes, s3Res, usersRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API}/admin/deploy-targets`),
          axios.get(`${process.env.REACT_APP_API}/admin/machines`).catch(() => ({ data: [] })),
          axios.get(`${process.env.REACT_APP_API}/admin/git-configurations`).catch(() => ({ data: [] })),
          axios.get(`${process.env.REACT_APP_API}/admin/build-configurations`).catch(() => ({ data: [] })),
          axios.get(`${process.env.REACT_APP_API}/admin/database-restore-configurations`).catch(() => ({ data: [] })),
          axios.get(`${process.env.REACT_APP_API}/admin/s3-configurations`).catch(() => ({ data: [] })),
          axios.get(`${process.env.REACT_APP_API}/admin/users`).catch(() => ({ data: [] })),
        ]);
        setAmbientes(targetsRes.data || []);
        const machineList = machinesRes.data || [];
        setMachines(machineList);
        setGitConfigs(gitRes.data || []);
        setBuildConfigs(buildRes.data || []);
        setRestoreConfigs(restoreRes.data || []);
        setS3Configs(s3Res.data || []);
        setUsers(usersRes.data || []);

        // Busca sites por máquina em paralelo e consolida numa lista com machineId
        const sitesPerMachine = await Promise.all(
          machineList.map((m) =>
            axios
              .get(`${process.env.REACT_APP_API}/iis/sites`, { params: { machineId: m.id } })
              .then((res) => (res.data || []).map((s) => ({ ...s, _machineId: m.id })))
              .catch(() => [])
          )
        );
        setSites(sitesPerMachine.flat());
      } catch (err) {
        const mensagem = err?.response?.data?.message || err?.message || "Erro ao carregar ambientes";
        AlertComponent("Erro", mensagem, { icon: "error" });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const editingTarget = useMemo(
    () => ambientes.find((a) => a.id === editingTargetId) || null,
    [ambientes, editingTargetId]
  );

  const handleTargetSaved = (updated) => {
    setAmbientes((prev) => prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a)));
  };

  const siteByMachineAndName = useMemo(() => {
    const map = new Map();
    for (const s of sites) {
      if (s?.name && s?._machineId != null) {
        map.set(`${s._machineId}:${s.name.toLowerCase()}`, s);
      }
    }
    return map;
  }, [sites]);

  const updateTarget = async (target, changes) => {
    if (savingId != null) return;
    const optimistic = { ...target, ...changes };
    setAmbientes((prev) => prev.map((a) => (a.id === target.id ? optimistic : a)));
    setSavingId(target.id);
    try {
      await axios.put(`${process.env.REACT_APP_API}/admin/deploy-targets/${target.id}`, {
        displayName: target.displayName,
        isProduction: optimistic.isProduction,
        isActive: optimistic.isActive,
        deploymentMachineId: target.deploymentMachineId,
        repositoryLocalPath: target.repositoryLocalPath ?? null,
        gitConfigurationId: target.gitConfigurationId ?? null,
        buildConfigurationId: target.buildConfigurationId ?? null,
        databaseRestoreConfigurationId: target.databaseRestoreConfigurationId ?? null,
        enableCodebaseBackup: !!target.enableCodebaseBackup,
        codebaseBackupS3ConfigurationId: target.enableCodebaseBackup
          ? target.codebaseBackupS3ConfigurationId ?? null
          : null,
        notificationRecipientUserIds: (target.notificationRecipients || [])
          .map((r) => r.userId)
          .filter((id) => id != null),
      });
    } catch (err) {
      setAmbientes((prev) => prev.map((a) => (a.id === target.id ? target : a)));
      const mensagem = err?.response?.data?.message || err?.message || "Erro ao atualizar ambiente";
      AlertComponent("Erro", mensagem, { icon: "error" });
    } finally {
      setSavingId(null);
    }
  };

  const controlSite = async (target, site, action) => {
    if (iisBusyId != null || !site?.name) return;

    if (action === "stop") {
      const isProd = target.isProduction;
      const result = await AlertComponent(
        isProd ? "Parar ambiente de PRODUÇÃO" : "Parar ambiente",
        `Tem certeza que deseja parar "${target.displayName || site.name}"?${isProd ? " Este é um ambiente produtivo — requisições em andamento serão interrompidas." : ""}`,
        {
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Sim, parar",
          cancelButtonText: "Cancelar",
          confirmButtonColor: "#dc2626",
          cancelButtonColor: "#64748b",
          reverseButtons: true,
        },
        null,
        false
      );
      if (!result?.isConfirmed) return;
    }

    setIisBusyId(target.id);
    try {
      const payload = { siteName: site.name };
      if (target.applicationPath) payload.applicationPath = target.applicationPath;
      await axios.post(`${process.env.REACT_APP_API}/iis/sites/toggle`, payload);

      const machineId = target.deploymentMachineId;
      if (machineId != null) {
        const res = await axios.get(`${process.env.REACT_APP_API}/iis/sites`, {
          params: { machineId },
        });
        const refreshed = (res.data || []).map((s) => ({ ...s, _machineId: machineId }));
        setSites((prev) => [
          ...prev.filter((s) => s._machineId !== machineId),
          ...refreshed,
        ]);
      }

      AlertComponent(
        "Sucesso",
        action === "start" ? "Ambiente iniciado" : "Ambiente parado",
        { icon: "success" }
      );
    } catch (err) {
      const mensagem = err?.response?.data?.message || err?.message || `Erro ao ${action === "start" ? "iniciar" : "parar"} ambiente`;
      AlertComponent("Erro", mensagem, { icon: "error" });
    } finally {
      setIisBusyId(null);
    }
  };


  const ambientesFiltrados = useMemo(
    () => ambientes.filter((a) => matchesFiltros(a, filtroTipo, filtroStatus, filtroMachine)),
    [ambientes, filtroTipo, filtroStatus, filtroMachine]
  );

  const contagemTipo = useMemo(
    () => ({
      todos: ambientes.length,
      producao: ambientes.filter((a) => a.isProduction).length,
      homologacao: ambientes.filter((a) => !a.isProduction).length,
    }),
    [ambientes]
  );

  const contagemStatus = useMemo(
    () => ({
      ativos: ambientes.filter((a) => a.isActive !== false).length,
      inativos: ambientes.filter((a) => a.isActive === false).length,
      todos: ambientes.length,
    }),
    [ambientes]
  );

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div
        className="rounded-2xl p-6 sm:p-8 mb-6 relative overflow-hidden text-white"
        style={{ background: "linear-gradient(135deg, #1e1b4b 0%, #3b0764 100%)" }}
      >
        <div className="absolute -top-10 -right-10 w-52 h-52 rounded-full opacity-10" style={{ background: "radial-gradient(circle,#a78bfa,transparent)" }} />
        <div className="absolute -bottom-12 -left-6 w-40 h-40 rounded-full opacity-10" style={{ background: "radial-gradient(circle,#c4b5fd,transparent)" }} />
        <div className="relative">
          <p className="text-purple-300 text-sm font-medium mb-1">Administração</p>
          <h1 className="text-2xl sm:text-3xl font-bold">Ambientes</h1>
          <p className="text-purple-300 text-sm mt-1">
            {loading
              ? "Carregando..."
              : `${ambientes.length} ambiente${ambientes.length !== 1 ? "s" : ""} cadastrado${ambientes.length !== 1 ? "s" : ""}`}
          </p>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex flex-wrap items-center gap-3">
          {machinesOrdenadas.length > 0 && (
            <div className="relative">
              <select
                value={filtroMachine}
                onChange={(e) => setFiltroMachine(e.target.value)}
                disabled={loading || machinesOrdenadas.length === 1}
                className="appearance-none rounded-xl bg-slate-100 hover:bg-slate-200 transition pl-4 pr-9 py-2 text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {machinesOrdenadas.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
              <span className="absolute inset-y-0 right-0 flex items-center pr-2.5 text-slate-400 pointer-events-none">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </span>
            </div>
          )}
          <FilterGroup
            filtros={FILTROS_TIPO}
            ativo={filtroTipo}
            onChange={setFiltroTipo}
            contagem={contagemTipo}
            loading={loading}
          />
        </div>
        <FilterGroup
          filtros={FILTROS_STATUS}
          ativo={filtroStatus}
          onChange={setFiltroStatus}
          contagem={contagemStatus}
          loading={loading}
        />
      </div>

      {/* Conteúdo */}
      {loading ? (
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-200/60 flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-violet-300 border-t-violet-600 rounded-full animate-spin" />
        </div>
      ) : ambientesFiltrados.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-200/60 text-center py-20 px-6">
          <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
            </svg>
          </div>
          <p className="text-slate-600 font-semibold mb-1">Nenhum ambiente encontrado</p>
          <p className="text-slate-400 text-sm">
            {ambientes.length === 0
              ? "Nenhum ambiente foi cadastrado ainda."
              : "Nenhum resultado para os filtros selecionados."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {ambientesFiltrados.map((a) => {
            const inativo = a.isActive === false;
            const site = a.deploymentMachineId != null && a.siteName
              ? siteByMachineAndName.get(`${a.deploymentMachineId}:${a.siteName.toLowerCase()}`)
              : null;
            const stateInfo = site ? stateStyle(site.state) : null;
            const bindings = Array.isArray(site?.bindings)
              ? site.bindings.map(parseBinding).filter(Boolean)
              : [];
            return (
              <div
                key={a.id}
                className={`bg-white rounded-2xl shadow-sm ring-1 overflow-hidden transition-all ${
                  inativo ? "ring-slate-200/60 opacity-75" : "ring-slate-200/60 hover:shadow-md hover:ring-violet-200"
                }`}
              >
                {/* Faixa superior */}
                <div
                  className="h-1.5"
                  style={{
                    background: inativo
                      ? "#cbd5e1"
                      : a.isProduction
                        ? "linear-gradient(90deg,#f43f5e,#e11d48)"
                        : "linear-gradient(90deg,#8b5cf6,#6d28d9)",
                  }}
                />

                <div className="p-5">
                  {/* Título + badges */}
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-base font-bold text-slate-900 truncate">
                          {a.displayName || a.siteName || `Ambiente #${a.id}`}
                        </h3>
                        {a.isProduction ? (
                          <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-rose-100 text-rose-700 ring-1 ring-rose-200">
                            Produção
                          </span>
                        ) : (
                          <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200">
                            Homologação
                          </span>
                        )}
                        {inativo && (
                          <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-slate-200 text-slate-600 ring-1 ring-slate-300">
                            Inativo
                          </span>
                        )}
                        {(a.databaseDiscovery?.enabled || a.usesDynamicDatabaseDiscovery) && (
                          <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-sky-100 text-sky-700 ring-1 ring-sky-200">
                            Multi-banco
                          </span>
                        )}
                        {Array.isArray(a.linkedTargets) && a.linkedTargets.length > 0 && (
                          <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-indigo-100 text-indigo-700 ring-1 ring-indigo-200">
                            Multi-site
                          </span>
                        )}
                        {stateInfo && (
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded ring-1 ${stateInfo.pill}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${stateInfo.dot}`} />
                            {stateInfo.label}
                          </span>
                        )}
                      </div>
                      {a.machineName && (
                        <p className="flex items-center gap-1.5 text-[11px] text-slate-500 mt-1.5">
                          <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12V7a2 2 0 012-2h10a2 2 0 012 2v5M3 19h18M5 12h14a2 2 0 012 2v3a2 2 0 01-2 2H5a2 2 0 01-2-2v-3a2 2 0 012-2zM7 16h.01M11 16h.01" />
                          </svg>
                          <span className="font-medium">{a.machineName}</span>
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Grid de detalhes */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                    <InfoItem
                      label="Servidor"
                      value={a.serverName}
                      icon={
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12H3l9-9 9 9h-2M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7M5 12l7-7 7 7" />
                        </svg>
                      }
                    />
                    <InfoItem
                      label="Site"
                      value={a.siteName}
                      mono
                      icon={
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                        </svg>
                      }
                    />
                    <InfoItem
                      label="Application Path"
                      value={a.applicationPath}
                      mono
                      icon={
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                      }
                    />
                    <InfoItem
                      label="Application Pool"
                      value={a.applicationPool}
                      mono
                      icon={
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2 1 3 3 3h10c2 0 3-1 3-3V7c0-2-1-3-3-3H7C5 4 4 5 4 7z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 4v16M16 4v16M4 8h4M4 16h4M16 8h4M16 16h4" />
                        </svg>
                      }
                    />
                    {a.physicalPath && (
                      <div className="sm:col-span-2">
                        <InfoItem
                          label="Caminho físico"
                          value={a.physicalPath}
                          mono
                          breakAll
                          icon={
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                            </svg>
                          }
                        />
                      </div>
                    )}
                  </div>

                  {/* Bindings do IIS */}
                  {bindings.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-violet-600 mb-2">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                        <span>Endereços</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {bindings.map((b, idx) => {
                          const url = bindingUrl(b);
                          const label = bindingLabel(b) || b.raw;
                          const isHttps = b.protocol === "https";
                          const pillClasses = isHttps
                            ? "bg-emerald-50 text-emerald-700 ring-emerald-200 hover:bg-emerald-100"
                            : "bg-slate-50 text-slate-600 ring-slate-200 hover:bg-slate-100";
                          const content = (
                            <>
                              <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                {isHttps
                                  ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                  : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />}
                              </svg>
                              <span className="truncate">{label}</span>
                            </>
                          );
                          return url ? (
                            <a
                              key={idx}
                              href={url}
                              target="_blank"
                              rel="noreferrer noopener"
                              title={`${b.protocol?.toUpperCase()} · porta ${b.port}`}
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-mono font-medium ring-1 transition-colors max-w-full ${pillClasses}`}
                            >
                              {content}
                            </a>
                          ) : (
                            <span
                              key={idx}
                              title={b.raw}
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-mono font-medium ring-1 max-w-full ${pillClasses}`}
                            >
                              {content}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Configurações (toggles inline) */}
                  <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 gap-3">
                    <ToggleField
                      label="Produção"
                      description={a.isProduction ? "Deploy produtivo" : "Homologação"}
                      checked={!!a.isProduction}
                      onChange={(v) => updateTarget(a, { isProduction: v })}
                      disabled={savingId != null}
                      loading={savingId === a.id}
                    />
                    <ToggleField
                      label="Ativo"
                      description={a.isActive === false ? "Não aparecerá em listagens" : "Disponível para deploy"}
                      checked={a.isActive !== false}
                      onChange={(v) => updateTarget(a, { isActive: v })}
                      disabled={savingId != null}
                      loading={savingId === a.id}
                    />
                  </div>

                  {/* Rodapé com último scan + controles */}
                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-1.5 text-[11px] text-slate-400 min-w-0">
                      <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      <span className="truncate">Último scan: {formatarDataHora(a.lastScannedAt)}</span>
                    </div>

                    <div className="flex items-center gap-2 shrink-0 ml-auto">
                      <button
                        type="button"
                        onClick={() => setEditingTargetId(a.id)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold uppercase tracking-wider ring-1 transition-colors bg-violet-50 text-violet-700 ring-violet-200 hover:bg-violet-100"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                        Editar
                      </button>

                    {site && (() => {
                      const stateLower = (site.state || "").toLowerCase();
                      const isStarted = stateLower === "started";
                      const isTransitioning = stateLower === "starting" || stateLower === "stopping";
                      const isBusy = iisBusyId === a.id;
                      const action = isStarted ? "stop" : "start";
                      const disabled = isBusy || isTransitioning || iisBusyId != null;
                      const pillClasses = isStarted
                        ? "bg-red-50 text-red-700 ring-red-200 hover:bg-red-100"
                        : "bg-emerald-50 text-emerald-700 ring-emerald-200 hover:bg-emerald-100";

                      return (
                        <button
                          type="button"
                          onClick={() => controlSite(a, site, action)}
                          disabled={disabled}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold uppercase tracking-wider ring-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0 ${pillClasses}`}
                        >
                          {isBusy ? (
                            <>
                              <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                              </svg>
                              {isStarted ? "Parando..." : "Iniciando..."}
                            </>
                          ) : isStarted ? (
                            <>
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <rect x="6" y="6" width="12" height="12" rx="1.5" strokeWidth={2} />
                              </svg>
                              Parar
                            </>
                          ) : (
                            <>
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z" />
                              </svg>
                              Iniciar
                            </>
                          )}
                        </button>
                      );
                    })()}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editingTarget && (
        <TargetSettingsModal
          target={editingTarget}
          machines={machines}
          gitConfigs={gitConfigs}
          buildConfigs={buildConfigs}
          restoreConfigs={restoreConfigs}
          s3Configs={s3Configs}
          targets={ambientes}
          users={users}
          onClose={() => setEditingTargetId(null)}
          onSaved={(updated) => {
            handleTargetSaved(updated);
            setEditingTargetId(null);
          }}
        />
      )}
    </div>
  );
}

function FilterGroup({ filtros, ativo, onChange, contagem, loading }) {
  return (
    <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl w-fit overflow-x-auto">
      {filtros.map((f) => (
        <button
          key={f.key}
          onClick={() => onChange(f.key)}
          className={`flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${
            ativo === f.key
              ? "bg-white text-slate-800 shadow-sm"
              : "text-slate-500 hover:text-slate-700"
          }`}
        >
          {f.label}
          <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
            ativo === f.key ? "bg-violet-100 text-violet-700" : "bg-slate-200 text-slate-500"
          }`}>
            {loading ? "—" : contagem[f.key]}
          </span>
        </button>
      ))}
    </div>
  );
}

function ToggleField({ label, description, checked, onChange, disabled, loading }) {
  return (
    <div className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg ring-1 ${
      checked ? "bg-violet-50/40 ring-violet-200" : "bg-slate-50/50 ring-slate-200"
    }`}>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-xs font-bold text-slate-700">{label}</p>
          {loading && (
            <svg className="animate-spin w-3 h-3 text-violet-500" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
          )}
        </div>
        {description && <p className="text-[10px] text-slate-500 truncate">{description}</p>}
      </div>
      <VioletSwitch checked={checked} onChange={onChange} disabled={disabled} />
    </div>
  );
}

function InfoItem({ label, value, icon, mono, breakAll }) {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-violet-600 mb-0.5">
        {icon}
        <span>{label}</span>
      </div>
      <p className={`text-xs text-slate-700 truncate ${mono ? "font-mono" : "font-medium"} ${breakAll ? "break-all whitespace-normal" : ""}`}>
        {value || "—"}
      </p>
    </div>
  );
}
